import type { EmiHistoryItem } from './types';

const STORAGE_KEY = 'ecregi_emi_history';
const MAX_HISTORY_ITEMS = 30;

export function loadEmiHistory(): EmiHistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.error('Failed to load EMI calculation history from localStorage', err);
    return [];
  }
}

export function saveEmiHistoryItem(
  item: Omit<EmiHistoryItem, 'id' | 'timestamp'>,
): EmiHistoryItem[] {
  try {
    const existing = loadEmiHistory();
    const newItem: EmiHistoryItem = {
      ...item,
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      timestamp: Date.now(),
    };

    // Avoid exact duplicate entries at the top
    const filtered = existing.filter(
      (h) =>
        !(
          h.mode === newItem.mode &&
          h.principal === newItem.principal &&
          h.rate === newItem.rate &&
          h.totalMonths === newItem.totalMonths &&
          h.emi === newItem.emi
        ),
    );

    const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (err) {
    console.error('Failed to save EMI calculation history to localStorage', err);
    return loadEmiHistory();
  }
}

export function deleteEmiHistoryItem(id: string): EmiHistoryItem[] {
  try {
    const existing = loadEmiHistory();
    const updated = existing.filter((item) => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (err) {
    console.error('Failed to delete EMI history item', err);
    return loadEmiHistory();
  }
}

export function clearEmiHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('Failed to clear EMI history', err);
  }
}

export function formatHistoryDate(ts: number): string {
  try {
    const d = new Date(ts);
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  } catch {
    return '';
  }
}
